/**
 * @license Copyright (c) 2003-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md or https://ckeditor.com/legal/ckeditor-licensing-options
 */

/**
 * @module paste-from-office/pastefromoffice
 */

import { Plugin } from 'ckeditor5/src/core.js';

import { ClipboardPipeline } from 'ckeditor5/src/clipboard.js';

import { PasteFromOfficeMSWordNormalizer } from './normalizers/mswordnormalizer.js';
import { GoogleDocsNormalizer } from './normalizers/googledocsnormalizer.js';
import { GoogleSheetsNormalizer } from './normalizers/googlesheetsnormalizer.js';

import { parsePasteOfficeHtml } from './filters/parse.js';
import type { PasteFromOfficeNormalizer, PasteFromOfficeNormalizerData } from './normalizer.js';

/**
 * The Paste from Office plugin.
 *
 * This plugin handles content pasted from Office apps and transforms it (if necessary)
 * to a valid structure which can then be understood by the editor features.
 *
 * Transformation is made by a set of predefined {@link module:paste-from-office/normalizer~PasteFromOfficeNormalizer normalizers}.
 * This plugin includes following normalizers:
 * * {@link module:paste-from-office/normalizers/mswordnormalizer~PasteFromOfficeMSWordNormalizer Microsoft Word normalizer}
 * * {@link module:paste-from-office/normalizers/googledocsnormalizer~GoogleDocsNormalizer Google Docs normalizer}
 *
 * For more information about this feature check the {@glink api/paste-from-office package page}.
 */
export class PasteFromOffice extends Plugin {
	/**
	 * @inheritDoc
	 */
	public static get pluginName() {
		return 'PasteFromOffice' as const;
	}

	/**
	 * @inheritDoc
	 * @internal
	 */
	public static get licenseFeatureCode(): string {
		return 'PFO';
	}

	/**
	 * @inheritDoc
	 */
	public static override get isOfficialPlugin(): true {
		return true;
	}

	/**
	 * @inheritDoc
	 */
	public static override get isPremiumPlugin(): true {
		return true;
	}

	/**
	 * @inheritDoc
	 */
	public static get requires() {
		return [ ClipboardPipeline ] as const;
	}

	/**
	 * @inheritDoc
	 */
	public init(): void {
		const editor = this.editor;
		const clipboardPipeline: ClipboardPipeline = editor.plugins.get( 'ClipboardPipeline' );
		const viewDocument = editor.editing.view.document;
		const normalizers: Array<PasteFromOfficeNormalizer> = [];
		const hasMultiLevelListPlugin = this.editor.plugins.has( 'MultiLevelList' );
		const hasTablePropertiesPlugin = this.editor.plugins.has( 'TableProperties' );
		const hasExtendedTableBlockAlignment = !!this.editor.config.get( 'experimentalFlags.useExtendedTableBlockAlignment' );

		normalizers.push(
			new PasteFromOfficeMSWordNormalizer(
				viewDocument,
				hasMultiLevelListPlugin,
				hasTablePropertiesPlugin,
				hasExtendedTableBlockAlignment
			)
		);
		normalizers.push( new GoogleDocsNormalizer( viewDocument ) );
		normalizers.push( new GoogleSheetsNormalizer( viewDocument ) );

		clipboardPipeline.on(
			'inputTransformation',
			( evt, data: PasteFromOfficeNormalizerData ) => {
				if ( data._isTransformedWithPasteFromOffice ) {
					return;
				}

				const codeBlock = editor.model.document.selection.getFirstPosition()!.parent;

				if ( codeBlock.is( 'element', 'codeBlock' ) ) {
					return;
				}

				const htmlString = data.dataTransfer.getData( 'text/html' );
				const activeNormalizer = normalizers.find( normalizer => normalizer.isActive( htmlString ) );

				if ( activeNormalizer ) {
					if ( !data._parsedData ) {
						data._parsedData = parsePasteOfficeHtml( htmlString, viewDocument.stylesProcessor );
					}

					activeNormalizer.execute( data );

					data._isTransformedWithPasteFromOffice = true;
				}
			},
			{ priority: 'high' }
		);
	}
}
